import { initialInterfaceAppearance } from '@/app/models/InterfaceAppearanceDto';
import { HEX_COLOR_PATTERN } from '@/app/consts/interfaceAppearance';

const getColorChannels = (color: string): [number, number, number] => [
  Number.parseInt(color.slice(1, 3), 16),
  Number.parseInt(color.slice(3, 5), 16),
  Number.parseInt(color.slice(5, 7), 16),
];

const getRelativeLuminance = (red: number, green: number, blue: number): number => {
  const linearChannels = [red, green, blue].map((channel) => {
    const normalizedChannel = channel / 255;
    return normalizedChannel <= 0.04045 ? normalizedChannel / 12.92 : ((normalizedChannel + 0.055) / 1.055) ** 2.4;
  });
  return linearChannels[0] * 0.2126 + linearChannels[1] * 0.7152 + linearChannels[2] * 0.0722;
};

export const getPrimaryColorTokens = (primaryColor?: string): Record<string, string> => {
  const colorIsValid = primaryColor !== undefined && HEX_COLOR_PATTERN.test(primaryColor);
  const color = colorIsValid ? primaryColor : initialInterfaceAppearance.primary_color;
  const [red, green, blue] = getColorChannels(color);
  const channels = [red, green, blue];
  const luminance = getRelativeLuminance(red, green, blue);
  const contrastColor = luminance > 0.179 ? '#000000' : '#ffffff';
  const rgb = `${red}, ${green}, ${blue}`;
  const darkenedChannels = channels.map((channel) => Math.round(channel * 0.85));
  const darkenedLuminance = getRelativeLuminance(darkenedChannels[0], darkenedChannels[1], darkenedChannels[2]);
  const darkenedContrast =
    contrastColor === '#000000' ? (darkenedLuminance + 0.05) / 0.05 : 1.05 / (darkenedLuminance + 0.05);
  const activeChannels =
    darkenedContrast >= 4.5 ? darkenedChannels : channels.map((channel) => Math.round(channel * 0.85 + 255 * 0.15));
  const activeRgb = activeChannels.join(', ');
  const lightTextChannels = luminance > 0.14 ? channels.map((channel) => Math.round(channel * 0.4)) : channels;
  const darkTextChannels =
    luminance < 0.4 ? channels.map((channel) => Math.round(channel * 0.4 + 255 * 0.6)) : channels;
  const lightTextRgb = lightTextChannels.join(', ');
  const darkTextRgb = darkTextChannels.join(', ');

  return {
    '--bs-primary': color,
    '--bs-primary-rgb': rgb,
    '--bs-primary-active': `rgb(${activeRgb})`,
    '--bs-primary-light': `color-mix(in srgb, ${color} 12%, var(--bs-body-bg))`,
    '--bs-primary-clarity': `rgba(${rgb}, 0.2)`,
    '--bs-primary-inverse': contrastColor,
    '--bs-primary-bg-subtle': 'var(--bs-primary-light)',
    '--bs-primary-border-subtle': `rgba(${rgb}, 0.3)`,
    '--bs-primary-text-emphasis': 'var(--app-primary-text)',
    '--app-primary-text-light': `rgb(${lightTextRgb})`,
    '--app-primary-text-light-rgb': lightTextRgb,
    '--app-primary-text-dark': `rgb(${darkTextRgb})`,
    '--app-primary-text-dark-rgb': darkTextRgb,
    '--app-primary-text': 'var(--app-primary-text-light)',
    '--app-primary-text-rgb': 'var(--app-primary-text-light-rgb)',
    '--bs-text-primary': 'var(--app-primary-text)',
    '--bs-component-active-bg': color,
    '--bs-component-active-color': contrastColor,
    '--bs-component-checked-bg': color,
    '--bs-component-checked-color': contrastColor,
    '--bs-component-hover-color': 'var(--app-primary-text)',
    '--bs-link-color': 'var(--app-primary-text)',
    '--bs-link-color-rgb': 'var(--app-primary-text-rgb)',
    '--bs-link-hover-color': 'var(--app-primary-text)',
    '--bs-link-hover-color-rgb': 'var(--app-primary-text-rgb)',
    '--bs-focus-ring-color': 'rgba(var(--app-primary-text-rgb), 0.25)',
  };
};

const getSidebarAccentColor = (
  primaryColor: string,
  sidebarLuminance: number,
  activeLuminance: number,
  foreground: string,
): string => {
  const channels = getColorChannels(primaryColor);
  const foregroundChannel = foreground === '#ffffff' ? 255 : 0;
  for (let step = 0; step <= 10; step += 1) {
    const mix = step / 10;
    const accentChannels = channels.map((channel) => Math.round(channel * (1 - mix) + foregroundChannel * mix));
    const accentLuminance = getRelativeLuminance(accentChannels[0], accentChannels[1], accentChannels[2]);
    const hasContrast = [sidebarLuminance, activeLuminance].every((backgroundLuminance) => {
      const lighter = Math.max(accentLuminance, backgroundLuminance);
      const darker = Math.min(accentLuminance, backgroundLuminance);
      return (lighter + 0.05) / (darker + 0.05) >= 4.5;
    });
    if (hasContrast) return step === 0 ? primaryColor : `rgb(${accentChannels.join(', ')})`;
  }
  return foreground;
};

export const getSidebarColorTokens = (sidebarColor?: string, primaryColor?: string): Record<string, string> => {
  const colorIsValid = sidebarColor !== undefined && HEX_COLOR_PATTERN.test(sidebarColor);
  const color = colorIsValid ? sidebarColor : initialInterfaceAppearance.sidebar_color;
  const [red, green, blue] = getColorChannels(color);
  const luminance = getRelativeLuminance(red, green, blue);
  const foreground = luminance > 0.179 ? '#000000' : '#ffffff';
  const activeColor = luminance > 0.179 ? '#ffffff' : '#000000';
  const activeChannel = activeColor === '#ffffff' ? 255 : 0;
  const activeChannels = [red, green, blue].map((channel) => Math.round(channel * 0.84 + activeChannel * 0.16));
  const activeLuminance = getRelativeLuminance(activeChannels[0], activeChannels[1], activeChannels[2]);
  const primaryColorIsValid = primaryColor !== undefined && HEX_COLOR_PATTERN.test(primaryColor);
  const accentSource = primaryColorIsValid ? primaryColor : initialInterfaceAppearance.primary_color;
  const accent = getSidebarAccentColor(accentSource, luminance, activeLuminance, foreground);

  return {
    '--app-sidebar-background': color,
    '--app-sidebar-foreground': foreground,
    '--app-sidebar-accent': accent,
    '--app-sidebar-active-background': `color-mix(in srgb, ${color} 84%, ${activeColor})`,
    '--app-sidebar-primary-text': luminance > 0.179 ? 'var(--app-primary-text-light)' : 'var(--app-primary-text-dark)',
  };
};

export const getInterfaceBackgroundColor = (backgroundColor?: string): string => {
  const colorIsValid = backgroundColor !== undefined && HEX_COLOR_PATTERN.test(backgroundColor);
  const color = colorIsValid ? backgroundColor : initialInterfaceAppearance.background_color;
  const [red, green, blue] = getColorChannels(color);
  const luminance = getRelativeLuminance(red, green, blue);
  // O modo claro mantém o primeiro plano nativo dos títulos e dos cartões.
  return luminance >= 0.5 ? color : `color-mix(in srgb, ${color} 12%, #ffffff)`;
};
